import type { AdminQueryError } from "../../contracts/admin-query-errors";
import type { AdminQueryPort } from "../../contracts/admin-query-port";
import type {
  AdminConversationContentAccessRequest,
  AdminConversationPage,
  AdminConversationQuery,
  AdminConversationTranscript,
  AdminDeviceKpiPage,
  AdminDeviceKpiQuery,
  AdminFailedFlowPage,
  AdminFailedFlowQuery,
  AdminIncidentLogArtifactAccessAction,
  AdminIncidentLogArtifactAccessGrant,
  AdminJobPage,
  AdminJobQuery,
  AdminMediaArtifactAccessAction,
  AdminMediaArtifactAccessGrant,
  AdminOverviewQuery,
  AdminOverviewSummary,
  AdminQueryEnvelope,
  AdminRecognitionAuditDetail,
  AdminRecognitionAuditPage,
  AdminRecognitionAuditQuery,
  AdminRecognitionRawAccessGrant,
  AdminRecognitionRawAccessRequest,
  AdminReleaseRegression,
  AdminReleaseRegressionQuery,
  AdminUserDataDetail,
  AdminUserLookupInput,
  AdminUserSummaryPage,
  AdminUserSummaryQuery,
  AdminUserSummary,
} from "../../contracts/admin-read-models";
import type { CommunityReport, HealthContentDraftInput, HealthContentVersion, Phase2AdminEnvelope, Phase2FeatureFlagState, Phase2FeatureKey, PublicCaseReviewPage, TagSimilarityCandidate, TagSimilarityRun } from "../../contracts/phase2-admin-contracts";
import { normalizeAdminQueryError } from "./normalize-admin-query-error";
import { normalizeAdminUserSummaryRuntimePayload } from "./admin-user-summary-runtime-payload";

export type HttpAdminQueryAdapterConfig = {
  apiBaseUrl: string;
  fetchFn?: typeof fetch;
  getAccessToken?: () => Promise<string | undefined> | string | undefined;
};

type EnvelopeRequestOptions = {
  path: string;
  method?: "GET" | "POST" | "PATCH";
  query?: Record<string, string | number | readonly string[] | readonly number[] | undefined>;
  body?: BodyInit;
  normalizeData?: (data: unknown) => unknown;
};

type ErrorEnvelope = {
  error?: {
    code?: string;
    message?: string;
    requestId?: string;
  };
};

function encodePathSegment(value: string): string {
  return encodeURIComponent(value);
}

function appendQueryValue(
  searchParams: URLSearchParams,
  key: string,
  value: string | number | readonly string[] | readonly number[] | undefined,
): void {
  if (value === undefined) {
    return;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      searchParams.append(key, String(item));
    }
    return;
  }

  searchParams.set(key, String(value));
}

function buildRequestUrl(
  baseUrl: string,
  path: string,
  query?: Record<string, string | number | readonly string[] | readonly number[] | undefined>,
): string {
  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(`${normalizedBaseUrl}${normalizedPath}`);

  if (query !== undefined) {
    for (const [key, value] of Object.entries(query)) {
      appendQueryValue(url.searchParams, key, value);
    }
  }

  return url.toString();
}

function toTimeRangeQuery(query: { timeRange: { from: string; to: string } }): Record<string, string> {
  return {
    from: query.timeRange.from,
    to: query.timeRange.to,
  };
}

function readErrorRequestId(body: unknown): string | undefined {
  if (typeof body !== "object" || body === null) {
    return undefined;
  }

  const envelope = body as ErrorEnvelope;
  return typeof envelope.error?.requestId === "string" ? envelope.error.requestId : undefined;
}

function isEnvelopeShape<T>(value: unknown): value is AdminQueryEnvelope<T> {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as Partial<AdminQueryEnvelope<T>>;
  return (
    "data" in candidate &&
    typeof candidate.meta?.requestId === "string" &&
    typeof candidate.meta?.generatedAt === "string" &&
    typeof candidate.meta?.sourceFreshness === "object" &&
    candidate.meta.sourceFreshness !== null
  );
}

function isPhase2Envelope<T>(value: unknown): value is Phase2AdminEnvelope<T> {
  return typeof value === "object" && value !== null && "data" in value && typeof (value as { requestId?: unknown }).requestId === "string";
}

async function readJsonBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type");

  if (contentType?.includes("application/json")) {
    return response.json();
  }

  const text = await response.text();
  return text.length > 0 ? text : undefined;
}

function isHtmlShellFallbackBody(body: unknown, contentType: string | null): body is string {
  if (!contentType?.includes("text/html") || typeof body !== "string") {
    return false;
  }

  const normalized = body.toLowerCase();
  return normalized.includes("<!doctype html") || normalized.includes("<div id=\"root\"");
}

function toInvalidLookupError(message: string): AdminQueryError {
  return {
    code: "ADMIN_QUERY_INVALID_FILTER",
    message,
  };
}

export function createHttpAdminQueryAdapter(config: HttpAdminQueryAdapterConfig): AdminQueryPort {
  const fetchFn = config.fetchFn ?? fetch;

  async function requestAdminEnvelope<T>(
    options: EnvelopeRequestOptions,
  ): Promise<AdminQueryEnvelope<T>> {
    const token = await config.getAccessToken?.();
    const headers = new Headers({
      Accept: "application/json",
    });

    if (typeof token === "string" && token.length > 0) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    if (options.body !== undefined) {
      headers.set("Content-Type", "application/json");
    }

    const response = await fetchFn(buildRequestUrl(config.apiBaseUrl, options.path, options.query), {
      method: options.method ?? "GET",
      headers,
      body: options.body,
    });
    const body = await readJsonBody(response);

    if (response.ok && isHtmlShellFallbackBody(body, response.headers.get("content-type"))) {
      throw normalizeAdminQueryError({
        status: 503,
        body: {
          error: {
            code: "ROUTE_NOT_FOUND",
            message:
              "Admin API base URL resolved to a static HTML host. Set VITE_ADMIN_API_BASE_URL to the deployed API service root or provide a same-origin /api/admin/* proxy.",
          },
        },
        message: "Admin query request resolved to a static HTML shell.",
      });
    }

    if (!response.ok) {
      throw normalizeAdminQueryError({
        status: response.status,
        requestId: readErrorRequestId(body),
        body,
        message: `Admin query request failed with status ${response.status}.`,
      });
    }

    if (!isEnvelopeShape<T>(body)) {
      throw normalizeAdminQueryError({
        status: response.status,
        body,
        message: "Admin query response envelope is invalid.",
      });
    }

    if (options.normalizeData) {
      return {
        ...body,
        data: options.normalizeData(body.data) as T,
      };
    }

    return body;
  }

  async function requestPhase2Envelope<T>(options: EnvelopeRequestOptions): Promise<Phase2AdminEnvelope<T>> {
    const token = await config.getAccessToken?.();
    const headers = new Headers({ Accept: "application/json" });
    if (typeof token === "string" && token.length > 0) headers.set("Authorization", `Bearer ${token}`);
    if (options.body !== undefined) headers.set("Content-Type", "application/json");
    const response = await fetchFn(buildRequestUrl(config.apiBaseUrl, options.path, options.query), { method: options.method ?? "GET", headers, body: options.body });
    const body = await readJsonBody(response);
    if (!response.ok) throw normalizeAdminQueryError({ status: response.status, requestId: readErrorRequestId(body), body, message: `Phase 2 admin request failed with status ${response.status}.` });
    if (!isPhase2Envelope<T>(body)) throw normalizeAdminQueryError({ status: response.status, body, message: "Phase 2 admin response envelope is invalid." });
    return body;
  }

  return {
    getPhase2FeatureFlags() {
      return requestPhase2Envelope<Phase2FeatureFlagState>({ path: "/api/admin/phase2-feature-flags" });
    },
    updatePhase2FeatureFlag(featureKey: Phase2FeatureKey, input) {
      return requestPhase2Envelope<Phase2FeatureFlagState>({ path: `/api/admin/phase2-feature-flags/${encodePathSegment(featureKey)}`, method: "PATCH", body: JSON.stringify(input) });
    },
    listPublicCaseReviewQueue(query = {}) {
      return requestPhase2Envelope<PublicCaseReviewPage>({ path: "/api/admin/public-case-versions/review-queue", query });
    },
    reviewPublicCaseVersion(versionId, input) {
      return requestPhase2Envelope<{ versionId: string; decision: string }>({ path: `/api/admin/public-case-versions/${encodePathSegment(versionId)}/quality-review`, method: "POST", body: JSON.stringify(input) });
    },
    listTagSimilarityRuns() {
      return requestPhase2Envelope<TagSimilarityRun[]>({ path: "/api/admin/symptom-tags/similarity-runs" });
    },
    generateTagSimilarityRun(localDate) {
      return requestPhase2Envelope<TagSimilarityRun>({ path: "/api/admin/symptom-tags/similarity-runs", method: "POST", body: JSON.stringify({ localDate }) });
    },
    listTagSimilarityCandidates(status) {
      return requestPhase2Envelope<TagSimilarityCandidate[]>({ path: "/api/admin/symptom-tags/candidates", query: { status } });
    },
    reviewTagSimilarityCandidate(candidateId, input) {
      return requestPhase2Envelope<{ candidateId: string; decision: string }>({ path: `/api/admin/symptom-tags/candidates/${encodePathSegment(candidateId)}/review`, method: "POST", body: JSON.stringify(input) });
    },
    listCommunityReports(query = {}) {
      return requestPhase2Envelope<CommunityReport[]>({ path: "/api/admin/community-reports", query });
    },
    reviewCommunityReport(reportId, input) {
      return requestPhase2Envelope<CommunityReport>({ path: `/api/admin/community-reports/${encodePathSegment(reportId)}/review`, method: "POST", body: JSON.stringify(input) });
    },
    listHealthContents(query = {}) {
      return requestPhase2Envelope<HealthContentVersion[]>({ path: "/api/admin/health-contents", query });
    },
    createHealthContent(input: HealthContentDraftInput) {
      return requestPhase2Envelope<HealthContentVersion>({ path: "/api/admin/health-contents", method: "POST", body: JSON.stringify(input) });
    },
    createHealthContentVersion(contentId, input) {
      return requestPhase2Envelope<HealthContentVersion>({ path: `/api/admin/health-contents/${encodePathSegment(contentId)}/versions`, method: "POST", body: JSON.stringify(input) });
    },
    submitHealthContent(versionId, reasonCode) {
      return requestPhase2Envelope<HealthContentVersion>({ path: `/api/admin/health-contents/versions/${encodePathSegment(versionId)}/submit`, method: "POST", body: JSON.stringify({ reasonCode }) });
    },
    reviewHealthContent(versionId, input) {
      return requestPhase2Envelope<HealthContentVersion>({ path: `/api/admin/health-contents/versions/${encodePathSegment(versionId)}/review`, method: "POST", body: JSON.stringify(input) });
    },
    expireHealthContent(versionId, reasonCode) {
      return requestPhase2Envelope<HealthContentVersion>({ path: `/api/admin/health-contents/versions/${encodePathSegment(versionId)}/expire`, method: "POST", body: JSON.stringify({ reasonCode }) });
    },
    async getOverviewSummary(
      query: AdminOverviewQuery,
    ): Promise<AdminQueryEnvelope<AdminOverviewSummary>> {
      return requestAdminEnvelope<AdminOverviewSummary>({
        path: "/api/admin/overview",
        query: {
          ...toTimeRangeQuery(query),
          feature: query.feature,
          useCase: query.useCase,
        },
      });
    },
    async searchConversations(
      query: AdminConversationQuery,
    ): Promise<AdminQueryEnvelope<AdminConversationPage>> {
      return requestAdminEnvelope<AdminConversationPage>({
        path: "/api/admin/conversations",
        query: {
          from: query.from,
          to: query.to,
          source: query.source,
          internalUserId: query.internalUserId,
          limit: query.limit,
        },
      });
    },
    async accessConversationContent(
      input: AdminConversationContentAccessRequest,
    ): Promise<AdminQueryEnvelope<AdminConversationTranscript>> {
      return requestAdminEnvelope<AdminConversationTranscript>({
        path: `/api/admin/conversations/${encodePathSegment(input.source)}/${encodePathSegment(input.sessionId)}/content-access`,
        method: "POST",
        body: JSON.stringify({
          purpose: input.purpose,
          reason: input.reason,
        }),
      });
    },
    async searchFailedFlows(
      query: AdminFailedFlowQuery,
    ): Promise<AdminQueryEnvelope<AdminFailedFlowPage>> {
      return requestAdminEnvelope<AdminFailedFlowPage>({
        path: "/api/admin/failed-flows",
        query: {
          ...toTimeRangeQuery(query),
          feature: query.feature,
          useCase: query.useCase,
          errorCode: query.errorCode,
          statusCode: query.statusCode,
          requestId: query.requestId,
          correlationId: query.correlationId,
          userIdHash: query.userIdHash,
          petIdHash: query.petIdHash,
          release: query.release,
          appVersion: query.appVersion,
          platform: query.platform,
          deviceInstallId: query.deviceInstallId,
          cursor: query.cursor,
          limit: query.limit,
        },
      });
    },
    async searchDeviceKpis(
      query: AdminDeviceKpiQuery,
    ): Promise<AdminQueryEnvelope<AdminDeviceKpiPage>> {
      return requestAdminEnvelope<AdminDeviceKpiPage>({
        path: "/api/admin/device-kpis",
        query: {
          ...toTimeRangeQuery(query),
          release: query.release,
          appVersion: query.appVersion,
          platform: query.platform,
          deviceInstallId: query.deviceInstallId,
          cursor: query.cursor,
          limit: query.limit,
        },
      });
    },
    async searchUserSummaries(
      query: AdminUserSummaryQuery,
    ): Promise<AdminQueryEnvelope<AdminUserSummaryPage>> {
      return requestAdminEnvelope<AdminUserSummaryPage>({
        path: "/api/admin/user-summaries",
        query: {
          ...toTimeRangeQuery(query),
          cursor: query.cursor,
          limit: query.limit,
        },
        normalizeData: (data) => {
          if (typeof data !== "object" || data === null || !("items" in data) || !Array.isArray((data as { items?: unknown[] }).items)) {
            return data;
          }
          return {
            ...(data as Record<string, unknown>),
            items: (data as { items: Array<Record<string, unknown>> }).items.map((item) =>
              typeof item === "object" && item !== null ? normalizeAdminUserSummaryRuntimePayload(item) : item,
            ),
          };
        },
      });
    },
    async getUserSummary(
      input: AdminUserLookupInput,
    ): Promise<AdminQueryEnvelope<AdminUserSummary>> {
      if (typeof input.internalUserId === "string" && input.internalUserId.length > 0) {
        return requestAdminEnvelope<AdminUserSummary>({
          path: `/api/admin/users/${encodePathSegment(input.internalUserId)}`,
          normalizeData: (data) =>
            typeof data === "object" && data !== null
              ? normalizeAdminUserSummaryRuntimePayload(data as Record<string, unknown>)
              : data,
        });
      }

      if (typeof input.userIdHash === "string" && input.userIdHash.length > 0) {
        return requestAdminEnvelope<AdminUserSummary>({
          path: `/api/admin/users/by-hash/${encodePathSegment(input.userIdHash)}`,
          normalizeData: (data) =>
            typeof data === "object" && data !== null
              ? normalizeAdminUserSummaryRuntimePayload(data as Record<string, unknown>)
              : data,
        });
      }

      if (
        (typeof input.requestId === "string" && input.requestId.length > 0) ||
        (typeof input.correlationId === "string" && input.correlationId.length > 0)
      ) {
        return requestAdminEnvelope<AdminUserSummary>({
          path: "/api/admin/users",
          query: {
            requestId: input.requestId,
            correlationId: input.correlationId,
          },
          normalizeData: (data) =>
            typeof data === "object" && data !== null
              ? normalizeAdminUserSummaryRuntimePayload(data as Record<string, unknown>)
              : data,
        });
      }

      throw toInvalidLookupError(
        "User summary lookup requires internalUserId, userIdHash, requestId, or correlationId.",
      );
    },
    async getUserDataDetail(
      input: AdminUserLookupInput,
    ): Promise<AdminQueryEnvelope<AdminUserDataDetail>> {
      if (typeof input.internalUserId === "string" && input.internalUserId.length > 0) {
        return requestAdminEnvelope<AdminUserDataDetail>({
          path: `/api/admin/users/${encodePathSegment(input.internalUserId)}/data`,
        });
      }

      throw toInvalidLookupError(
        "User data detail lookup requires internalUserId in AD-P0-003.",
      );
    },
    async issueUserMediaArtifactAccess(input: {
      internalUserId: string;
      artifactId: string;
      action: AdminMediaArtifactAccessAction;
    }): Promise<AdminQueryEnvelope<AdminMediaArtifactAccessGrant>> {
      if (input.internalUserId.length === 0 || input.artifactId.length === 0) {
        throw toInvalidLookupError(
          "Admin media artifact access requires internalUserId and artifactId.",
        );
      }

      return requestAdminEnvelope<AdminMediaArtifactAccessGrant>({
        path: `/api/admin/users/${encodePathSegment(input.internalUserId)}/media/${encodePathSegment(input.artifactId)}/access`,
        query: {
          action: input.action,
        },
      });
    },
    async issueUserIncidentLogArtifactAccess(input: {
      internalUserId: string;
      artifactId: string;
      action: AdminIncidentLogArtifactAccessAction;
    }): Promise<AdminQueryEnvelope<AdminIncidentLogArtifactAccessGrant>> {
      if (input.internalUserId.length === 0 || input.artifactId.length === 0) {
        throw toInvalidLookupError(
          "Admin incident log artifact access requires internalUserId and artifactId.",
        );
      }

      return requestAdminEnvelope<AdminIncidentLogArtifactAccessGrant>({
        path: `/api/admin/users/${encodePathSegment(input.internalUserId)}/incidents/${encodePathSegment(input.artifactId)}/access`,
        query: {
          action: input.action,
        },
      });
    },
    async searchJobs(query: AdminJobQuery): Promise<AdminQueryEnvelope<AdminJobPage>> {
      return requestAdminEnvelope<AdminJobPage>({
        path: "/api/admin/jobs",
        query: {
          ...toTimeRangeQuery(query),
          jobType: query.jobType,
          status: query.status,
          userIdHash: query.userIdHash,
          petIdHash: query.petIdHash,
          linkedFileId: query.linkedFileId,
          linkedRecordId: query.linkedRecordId,
          requestId: query.requestId,
          correlationId: query.correlationId,
          release: query.release,
          appVersion: query.appVersion,
          cursor: query.cursor,
          limit: query.limit,
        },
      });
    },
    async getReleaseRegression(
      query: AdminReleaseRegressionQuery,
    ): Promise<AdminQueryEnvelope<AdminReleaseRegression>> {
      return requestAdminEnvelope<AdminReleaseRegression>({
        path: "/api/admin/releases",
        query: {
          ...toTimeRangeQuery(query),
          release: query.release,
          appVersion: query.appVersion,
          platform: query.platform,
          cursor: query.cursor,
          limit: query.limit,
        },
      });
    },
    async searchRecognitionAudits(
      query: AdminRecognitionAuditQuery,
    ): Promise<AdminQueryEnvelope<AdminRecognitionAuditPage>> {
      return requestAdminEnvelope<AdminRecognitionAuditPage>({
        path: "/api/admin/recognition-audits",
        query: {
          accountId: query.accountId,
          petId: query.petId,
          documentKind: query.documentKinds,
          status: query.statuses,
          outcome: query.outcomes,
          failedStage: query.failedStage,
          errorCode: query.errorCode,
          aiProvider: query.aiProvider,
          aiModel: query.aiModel,
          sourceFreshness: query.sourceFreshness,
          ...toTimeRangeQuery(query),
          limit: query.limit,
          cursor: query.cursor,
          sort: query.sort,
        },
      });
    },
    async getRecognitionAuditDetail(input: {
      recognitionRunId: string;
    }): Promise<AdminQueryEnvelope<AdminRecognitionAuditDetail>> {
      return requestAdminEnvelope<AdminRecognitionAuditDetail>({
        path: `/api/admin/recognition-audits/${encodePathSegment(input.recognitionRunId)}`,
      });
    },
    async issueRecognitionRawAccess(
      input: AdminRecognitionRawAccessRequest,
    ): Promise<AdminQueryEnvelope<AdminRecognitionRawAccessGrant>> {
      return requestAdminEnvelope<AdminRecognitionRawAccessGrant>({
        path: `/api/admin/recognition-audits/${encodePathSegment(input.recognitionRunId)}/raw-access`,
        method: "POST",
        body: JSON.stringify({
          targetKind: input.targetKind,
          targetId: input.targetId,
          purpose: input.purpose,
          reason: input.reason,
          linkedIssueId: input.linkedIssueId,
        }),
      });
    },
  };
}
